import React from "react";
import WebProfitSpinner from "../../../components/system/WebProfitSpinner";
import {
  Alert,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddRoundedIcon from "@mui/icons-material/AddRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import DeleteForeverRoundedIcon from "@mui/icons-material/DeleteForeverRounded";
import EditRoundedIcon from "@mui/icons-material/EditRounded";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import SearchRoundedIcon from "@mui/icons-material/SearchRounded";
import VisibilityRoundedIcon from "@mui/icons-material/VisibilityRounded";
import { useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { erpApi } from "../../../api/erp";
import MovementDetailDialog from "../../../components/dashboard/erp/MovementDetailDialog";
import MovementFormDialog from "../../../components/dashboard/erp/MovementFormDialog";
import { ErpEmpty, ErpLoading, ErpPanel, ErpSectionTitle, MovementStatusChip } from "../../../components/dashboard/erp/ErpUi";
import ResponsiveListControls from "../../../components/dashboard/ResponsiveListControls";
import ResponsiveOperationalTable from "../../../components/dashboard/ResponsiveOperationalTable";
import {
  ERP_BRAND,
  MOVEMENT_KIND_LABELS,
  entityId,
  formatDateTime,
  formatMoney,
} from "../../../utils/erp";
import { hasAnyProjectPermission, hasProjectPermission } from "../../../utils/projectAccess";
import { isProjectCapabilityEnabled } from "../../../utils/projectCapabilities";
import { responsiveActionsSx } from "../../../theme/webProfitResponsive";

const KIND_OPTIONS = ["sale", "purchase", "expense", "opening_balance", "stock_in", "stock_out", "stock_adjustment", "collection", "payment", "transfer", "cash_in", "cash_out", "reversal"];

function pendingPurchaseReceiptUnits(movement) {
  if (movement?.kind !== "purchase") return 0;
  return (movement.items || []).reduce(
    (sum, item) => sum + (item.stockReceiptStatus === "pending" ? Math.max(0, Number(item.stockDelta || 0)) : 0),
    0,
  );
}

export default function ProjectErpMovementsPage() {
  const { projectId } = useParams();
  const { project, bootstrap, refreshBootstrap, access } = useOutletContext();
  const canCreate = hasAnyProjectPermission(access, [
    "erp.sales.create",
    "erp.purchases.create",
    "erp.expenses.create",
    "erp.stock.manage",
    "erp.collections.create",
    "erp.payments.create",
    "cash.operations.create",
    "cash.operations.in",
    "cash.operations.out",
  ]);
  const canCreateSale = hasProjectPermission(access, "erp.sales.create");
  const canReadCustomers = hasProjectPermission(access, "customers.read");
  const membershipsEnabled = isProjectCapabilityEnabled(project, "memberships");
  const needsItemsSetup = bootstrap.progress?.itemCount === 0;
  const needsMoneySetup = !(bootstrap.registers || []).length;
  const canUpdate = hasProjectPermission(access, "erp.movements.update");
  const canConfirm = hasProjectPermission(access, "erp.movements.confirm");
  const canVoid = hasProjectPermission(access, "erp.movements.void");
  const [searchParams, setSearchParams] = useSearchParams();
  const salesView = searchParams.get("vista") === "ventas";
  const canCreateInView = salesView ? canCreateSale : canCreate;
  const [movements, setMovements] = React.useState([]);
  const [total, setTotal] = React.useState(0);
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(15);
  const [filters, setFilters] = React.useState({
    q: "",
    kind: salesView ? "sale" : "",
    status: "",
    dateFrom: "",
    dateTo: "",
    sort: "created_desc",
  });
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState("");
  const [formOpen, setFormOpen] = React.useState(searchParams.get("nuevo") === "1");
  const initialKind = searchParams.get("tipo") || (salesView ? "sale" : "");
  const initialProductId = searchParams.get("itemId") || "";
  const [editing, setEditing] = React.useState(null);
  const [detailId, setDetailId] = React.useState(searchParams.get("movementId") || "");
  const [action, setAction] = React.useState(null);
  const [actionReason, setActionReason] = React.useState("");
  const [processing, setProcessing] = React.useState(false);

  const load = React.useCallback(async () => {
    try {
      setLoading(true);
      setError("");
      const data = await erpApi.listMovements(projectId, {
        page: page + 1,
        pageSize: rowsPerPage,
        ...filters,
      });
      setMovements(data.movements || []);
      setTotal(Number(data.total || 0));
    } catch (err) {
      setError(err.message || "No se pudieron cargar los movimientos");
      setMovements([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }, [projectId, page, rowsPerPage, filters]);

  React.useEffect(() => {
    const timeout = setTimeout(load, filters.q ? 300 : 0);
    return () => clearTimeout(timeout);
  }, [load, filters.q]);

  React.useEffect(() => {
    if (searchParams.get("nuevo") === "1") setFormOpen(true);
    const requestedMovementId = searchParams.get("movementId") || "";
    if (requestedMovementId) setDetailId(requestedMovementId);
  }, [searchParams]);

  React.useEffect(() => {
    setPage(0);
    setFilters((current) => {
      if (salesView) {
        return current.kind === "sale" ? current : { ...current, kind: "sale" };
      }
      return current.kind === "sale" ? { ...current, kind: "" } : current;
    });
  }, [salesView]);

  const setFilter = (key, value) => {
    setPage(0);
    setFilters((current) => ({ ...current, [key]: value }));
  };

  const hasActiveRefinement = Boolean(
    filters.q.trim()
      || filters.status
      || filters.dateFrom
      || filters.dateTo
      || (!salesView && filters.kind)
  );

  const resetListView = () => {
    setPage(0);
    setFilters((current) => ({
      ...current,
      q: "",
      kind: salesView ? "sale" : "",
      status: "",
      dateFrom: "",
      dateTo: "",
    }));
  };

  const closeForm = () => {
    setFormOpen(false);
    setEditing(null);
    if (searchParams.has("nuevo") || searchParams.has("tipo") || searchParams.has("itemId")) {
      const next = new URLSearchParams(searchParams);
      next.delete("nuevo");
      next.delete("tipo");
      next.delete("itemId");
      setSearchParams(next, { replace: true });
    }
  };

  const closeDetail = () => {
    setDetailId("");
    if (!searchParams.has("movementId")) return;
    const next = new URLSearchParams(searchParams);
    next.delete("movementId");
    setSearchParams(next, { replace: true });
  };

  const performAction = async () => {
    if (!action?.movement) return;
    try {
      setProcessing(true);
      setError("");
      const id = entityId(action.movement);
      if (action.type === "confirm") {
        await erpApi.confirmMovement(projectId, id);
      } else {
        if (!actionReason.trim()) {
          setError("Indicá el motivo de la anulación.");
          return;
        }
        await erpApi.voidMovement(projectId, id, actionReason);
      }
      setAction(null);
      setActionReason("");
      await Promise.all([load(), refreshBootstrap()]);
    } catch (err) {
      setError(err.message || "No se pudo procesar el movimiento");
    } finally {
      setProcessing(false);
    }
  };

  return (
    <Stack spacing={2.3}>
      {error ? <Alert severity="error" onClose={() => setError("")}>{error}</Alert> : null}

      <ErpSectionTitle
        title={salesView ? "Ventas registradas" : "Lo que pasó en tu negocio"}
        description={salesView
          ? "Ventas registradas en Gestión. Los pedidos web siguen separados en Pedidos."
          : "Ventas, compras, gastos, cobros, transferencias y stock en un solo historial."}
        action={
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={responsiveActionsSx}>
            <Button variant="outlined" startIcon={<RefreshRoundedIcon />} onClick={load} sx={{ textTransform: "none", fontWeight: 850 }}>
              Actualizar
            </Button>
            {canCreateInView ? (
              <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)} sx={{ textTransform: "none", fontWeight: 850 }}>
                {salesView ? "Registrar venta" : "Registrar algo"}
              </Button>
            ) : null}
          </Stack>
        }
      />

      {needsItemsSetup ? (
        <Alert
          severity="warning"
          action={(
            <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/items?crear=1`}>
              Cargar ítems
            </Button>
          )}
        >
          Antes de registrar operaciones, cargá los productos, servicios, insumos y gastos que usa el negocio.
        </Alert>
      ) : needsMoneySetup ? (
        <Alert
          severity="warning"
          action={(
            <Stack direction={{ xs: "column", sm: "row" }} spacing={0.5}>
              <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/caja?crear=1`}>
                Cajas y cuentas
              </Button>
              <Button color="inherit" href={`/dashboard/proyectos/${projectId}/sistema/cuentas`}>
                Plan contable
              </Button>
            </Stack>
          )}
        >
          Antes del primer movimiento de dinero, configurá Efectivo, bancos o billeteras, revisá sus cuentas contables y definí los saldos iniciales que correspondan.
        </Alert>
      ) : null}

      <ErpPanel>
        <Box sx={{ p: { xs: 1.1, sm: 2.2 }, borderBottom: `1px solid ${ERP_BRAND.border}` }}>
          <ResponsiveListControls
            search={(
              <TextField
                size="small"
                fullWidth
                placeholder="Buscar movimiento o referencia"
                value={filters.q}
                onChange={(event) => setFilter("q", event.target.value)}
                InputProps={{ startAdornment: <InputAdornment position="start"><SearchRoundedIcon /></InputAdornment> }}
              />
            )}
            activeFilterCount={[
              !salesView && filters.kind,
              filters.status,
              filters.dateFrom,
              filters.dateTo,
            ].filter(Boolean).length}
            onClear={() => {
              setFilters((current) => ({
                ...current,
                kind: salesView ? "sale" : "",
                status: "",
                dateFrom: "",
                dateTo: "",
              }));
              setPage(0);
            }}
            drawerTitle="Filtrar movimientos"
          >
            <TextField
              select
              size="small"
              label="Orden de creación"
              value={filters.sort}
              onChange={(event) => setFilter("sort", event.target.value)}
              sx={{ minWidth: { sm: 210 } }}
            >
              <MenuItem value="created_desc">Más nuevos primero</MenuItem>
              <MenuItem value="created_asc">Más antiguos primero</MenuItem>
            </TextField>
            <TextField select size="small" label="Tipo" value={filters.kind} onChange={(event) => setFilter("kind", event.target.value)} disabled={salesView} sx={{ minWidth: { sm: 190 } }}>
              <MenuItem value="">Todos</MenuItem>
              {KIND_OPTIONS.map((kind) => <MenuItem key={kind} value={kind}>{MOVEMENT_KIND_LABELS[kind]}</MenuItem>)}
            </TextField>
            <TextField select size="small" label="Estado" value={filters.status} onChange={(event) => setFilter("status", event.target.value)} sx={{ minWidth: { sm: 150 } }}>
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="draft">Borrador</MenuItem>
              <MenuItem value="confirmed">Confirmado</MenuItem>
              <MenuItem value="voided">Anulado</MenuItem>
            </TextField>
            <TextField size="small" type="date" label="Desde" value={filters.dateFrom} onChange={(event) => setFilter("dateFrom", event.target.value)} InputLabelProps={{ shrink: true }} />
            <TextField size="small" type="date" label="Hasta" value={filters.dateTo} onChange={(event) => setFilter("dateTo", event.target.value)} InputLabelProps={{ shrink: true }} />
          </ResponsiveListControls>
        </Box>

        {loading ? <ErpLoading label="Cargando movimientos..." /> : error ? null : !movements.length ? (
          <ErpEmpty
            title={hasActiveRefinement
              ? (salesView ? "No encontramos ventas con esta búsqueda" : "No encontramos movimientos con esta búsqueda")
              : (salesView ? "Todavía no hay ventas registradas" : "Todavía no hay movimientos registrados")}
            description={hasActiveRefinement
              ? "Probá con otro término o volvé a ver todos los registros."
              : salesView
                ? "Cuando registres una venta desde Gestión, aparecerá acá sin mezclarla con los pedidos recibidos desde la web."
                : "Ventas, compras, gastos, cobros, transferencias y movimientos de stock aparecerán acá cuando los registres."}
            action={hasActiveRefinement
              ? <Button variant="contained" onClick={resetListView}>Ver todos</Button>
              : canCreateInView
                ? <Button variant="contained" startIcon={<AddRoundedIcon />} onClick={() => setFormOpen(true)}>{salesView ? "Registrar venta" : "Registrar algo"}</Button>
                : null}
          />
        ) : (
          <>
            <TableContainer sx={{ maxWidth: "100%", overflowX: "auto" }}>
              <ResponsiveOperationalTable
                size="small"
                mobileLabel={salesView ? "Ventas registradas" : "Movimientos"}
                mobileMode="scroll"
                sx={{
                  minWidth: 1080,
                  "& .MuiTableCell-root": { py: 1.25, verticalAlign: "middle" },
                  "& .wp-movements-number-cell": {
                    position: { xs: "sticky", md: "static" },
                    left: 0,
                    zIndex: { xs: 2, md: "auto" },
                    bgcolor: "var(--wp-panel-surface, #fff)",
                    boxShadow: { xs: "6px 0 12px rgba(0,0,0,.06)", md: "none" },
                  },
                  "& thead .wp-movements-number-cell": { zIndex: { xs: 3, md: "auto" } },
                  "& .wp-movements-actions-cell": {
                    position: "sticky",
                    right: 0,
                    zIndex: 3,
                    minWidth: 142,
                    bgcolor: "var(--wp-panel-surface, #fff)",
                    boxShadow: "-8px 0 14px rgba(0,0,0,.07)",
                    whiteSpace: "nowrap",
                  },
                  "& thead .wp-movements-actions-cell": {
                    zIndex: 6,
                    bgcolor: "var(--wp-panel-surface-soft, #F7F8FB)",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell className="wp-movements-number-cell" sx={{ minWidth: 150 }}>Movimiento</TableCell>
                    <TableCell sx={{ minWidth: 150 }}>Creado</TableCell>
                    <TableCell sx={{ minWidth: 135 }}>Tipo</TableCell>
                    <TableCell sx={{ minWidth: 230 }}>Descripción</TableCell>
                    <TableCell sx={{ minWidth: 170 }}>Tercero</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right">Pendiente</TableCell>
                    <TableCell>Estado</TableCell>
                    <TableCell align="right" className="wp-movements-actions-cell">Acciones</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {movements.map((movement) => (
                    <TableRow key={entityId(movement)} hover>
                      <TableCell className="wp-movements-number-cell">
                        <Typography sx={{ fontWeight: 900, color: ERP_BRAND.dark }}>{movement.movementNumber}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Operación: {formatDateTime(movement.occurredAt)}</Typography>
                      </TableCell>
                      <TableCell>
                        <Typography sx={{ fontWeight: 800, color: ERP_BRAND.dark }}>{formatDateTime(movement.createdAt)}</Typography>
                        <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Fecha de alta</Typography>
                      </TableCell>
                      <TableCell>
                        <Stack spacing={0.55} alignItems="flex-start">
                          <Chip size="small" label={MOVEMENT_KIND_LABELS[movement.kind] || movement.kind} />
                          {pendingPurchaseReceiptUnits(movement) > 0 ? (
                            <Chip
                              size="small"
                              color="warning"
                              variant="outlined"
                              label={`${pendingPurchaseReceiptUnits(movement)} en tránsito`}
                            />
                          ) : null}
                        </Stack>
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Typography noWrap sx={{ fontWeight: 750 }}>{movement.description || "—"}</Typography>
                        {movement.sourceNumber ? <Typography variant="caption" sx={{ color: ERP_BRAND.muted }}>Ref. {movement.sourceNumber}</Typography> : null}
                      </TableCell>
                      <TableCell>{movement.party?.name || "—"}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 900 }}>{formatMoney(movement.total, movement.currency)}</TableCell>
                      <TableCell align="right" sx={{ color: Number(movement.balanceDue) > 0 ? "warning.main" : ERP_BRAND.muted, fontWeight: 850 }}>
                        {formatMoney(movement.balanceDue, movement.currency)}
                      </TableCell>
                      <TableCell><MovementStatusChip status={movement.status} /></TableCell>
                      <TableCell align="right" className="wp-movements-actions-cell">
                        <Tooltip title="Ver detalle"><IconButton size="small" onClick={() => setDetailId(entityId(movement))}><VisibilityRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        {movement.status === "draft" && (canUpdate || canConfirm) ? (
                          <>
                            {canUpdate ? <Tooltip title="Editar borrador"><IconButton size="small" onClick={() => { setEditing(movement); setFormOpen(true); }}><EditRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                            {canConfirm ? <Tooltip title="Confirmar movimiento"><IconButton size="small" color="success" onClick={() => setAction({ type: "confirm", movement })}><CheckCircleRoundedIcon fontSize="small" /></IconButton></Tooltip> : null}
                          </>
                        ) : null}
                        {movement.status === "confirmed" && canVoid ? (
                          <Tooltip title="Anular mediante reversa"><IconButton size="small" color="error" onClick={() => setAction({ type: "void", movement })}><DeleteForeverRoundedIcon fontSize="small" /></IconButton></Tooltip>
                        ) : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </ResponsiveOperationalTable>
            </TableContainer>
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_event, nextPage) => setPage(nextPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => { setRowsPerPage(Number(event.target.value)); setPage(0); }}
              rowsPerPageOptions={[10, 15, 25, 50]}
              labelRowsPerPage="Filas por página"
            />
          </>
        )}
      </ErpPanel>

      <MovementFormDialog
        open={formOpen}
        onClose={closeForm}
        onSaved={async () => {
          await Promise.all([load(), refreshBootstrap()]);
        }}
        projectId={projectId}
        movement={editing}
        bootstrap={bootstrap}
        access={access}
        initialKind={initialKind}
        initialProductId={initialProductId}
      />

      <MovementDetailDialog
        open={Boolean(detailId)}
        onClose={closeDetail}
        projectId={projectId}
        movementId={detailId}
        canReadCustomers={canReadCustomers && membershipsEnabled}
        canReceiveStock={hasProjectPermission(access, "erp.stock.manage")}
        onStockReceived={async () => {
          await Promise.all([load(), refreshBootstrap()]);
        }}
      />

      <Dialog open={Boolean(action)} onClose={processing ? undefined : () => setAction(null)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ fontWeight: 950 }}>
          {action?.type === "confirm" ? "Confirmar movimiento" : "Anular movimiento"}
        </DialogTitle>
        <Divider />
        <DialogContent>
          {action?.type === "confirm" ? (
            <Alert severity="warning">
              Al confirmar se aplicarán stock, asientos contables, transacciones y caja. Luego no podrá editarse; cualquier corrección se realiza mediante una reversa.
            </Alert>
          ) : (
            <Stack spacing={2}>
              <Alert severity="warning">
                La anulación no borra el registro. Generará un movimiento inverso para conservar la trazabilidad.
              </Alert>
              <TextField
                fullWidth
                required
                multiline
                minRows={3}
                label="Motivo de la anulación"
                value={actionReason}
                onChange={(event) => setActionReason(event.target.value)}
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, "& .MuiButton-root": { minHeight: 44 } }}>
          <Button onClick={() => setAction(null)} disabled={processing}>Cancelar</Button>
          <Button variant="contained" color={action?.type === "confirm" ? "success" : "error"} onClick={performAction} disabled={processing} startIcon={processing ? <WebProfitSpinner size={16} /> : undefined}>
            {processing ? "Procesando..." : action?.type === "confirm" ? "Confirmar" : "Generar reversa"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
